import React, { Dispatch, SetStateAction } from "react";
import {
  FaRedo,
  FaUndo,
  FaRegWindowMinimize,
  FaStar,
  FaExpand,
  FaCompress,
  FaSyncAlt,
  FaStream,
  FaListUl,
} from "react-icons/fa";
import Quill from "quill";
import {
  bulletizeTextAiResponse,
  expandTextAiResponse,
  rephraseTextAiResponse,
  shortenTextAiResponse,
  simplifyTextAiResponse,
} from "../../../services/getAiResponse";

let setGlobalAiLoading: ((loading: boolean) => void) | null = null;

// Type declarations for Quill
declare module "quill" {
  interface History {
    undo(): void;
    redo(): void;
  }

  interface Quill {
    history: History;
  }

  namespace Parchment {
    interface Blot { }
  }
}

// Custom Button Components
const UndoButton = () => <FaUndo />;
const RedoButton = () => <FaRedo />;
const DividerButton = () => <FaRegWindowMinimize />;
const StarButton = () => <FaStar />;

// Quill Handlers
function undoChange(this: { quill: Quill }) {
  this.quill.history.undo();
}

function redoChange(this: { quill: Quill }) {
  this.quill.history.redo();
}

const colorsList = [
  "#000000",
  "#e60000",
  "#ff9900",
  "#ffff00",
  "#008a00",
  "#0066cc",
  "#9933ff",
  "#ffffff",
  "#facccc",
  "#ffebcc",
  "#ffffcc",
  "#cce8cc",
  "#cce0f5",
  "#ebd6ff",
  "#bbbbbb",
  "#f06666",
  "#ffc266",
  "#ffff66",
  "#66b966",
  "#66a3e0",
  "#c285ff",
  "#888888",
  "#a10000",
  "#b26b00",
  "#b2b200",
  "#006100",
  "#0047b2",
  "#6b24b2",
  "#444444",
  "#5c0000",
  "#663d00",
  "#666600",
  "#003700",
  "#002966",
  "#3d1466",
  "#361999",
  "#ffebe9",
  "#1b1839",
];

// Divider Blot
const BlockEmbed: any = Quill.import("blots/block/embed");
class DividerBlot extends BlockEmbed {
  static blotName = "divider";
  static tagName = "hr";
}
Quill.register(DividerBlot);

function addDivider(this: { quill: Quill }) {
  const range = this.quill.getSelection(true);
  this.quill.insertText(range.index, "\n", "user");
  this.quill.insertEmbed(range.index + 1, "divider", true, "user");
  this.quill.setSelection(range.index + 2, 0, "silent");
}

// Emphasis Blot
const Inline: any = Quill.import("blots/inline");
class EmphBlot extends Inline {
  static blotName = "em";
  static tagName = "em";
  static className = "custom-em";
}
Quill.register(EmphBlot);

function addBorder(this: { quill: Quill }) {
  const range = this.quill.getSelection();
  if (range) this.quill.format("em", true);
}

function insertStar(this: { quill: Quill }) {
  const selection = this.quill.getSelection();
  const cursorPosition = selection ? selection.index : 0;
  this.quill.insertText(cursorPosition, "★");
  this.quill.setSelection(cursorPosition + 1, 0);
}

// Font Whitelist
const Font: any = Quill.import("formats/font");
Font.whitelist = [
  "arial",
  "comic-sans",
  "courier-new",
  "georgia",
  "helvetica",
  "lucida",
];
Quill.register(Font, true);

// AI Transformation Handler
const handleAiTransformation = async (
  quill: Quill,
  action: "expand" | "shorten" | "rephrase" | "simplify" | "bulletize",
  apiHandler: (text: string) => Promise<string>,
  setShowError?: Dispatch<SetStateAction<boolean>>
) => {
  const range = quill.getSelection();
  if (!range || range.length === 0) return;

  const text = quill.getText(range.index, range.length);
  if (text.length < 20 || text.length > 800) {
    if (setShowError) setShowError(true);
    else alert("Selected text must be between 20 and 800 characters.");
    return;
  }

  try {
    if (setGlobalAiLoading) setGlobalAiLoading(true);

    const transformedText = await apiHandler(text);

    quill.deleteText(range.index, range.length);
    quill.insertText(range.index, transformedText);
    quill.setSelection(range.index + transformedText.length, 0);
  } catch (err) {
    console.error(`Failed to ${action} text.`, err);
  } finally {
    if (setGlobalAiLoading) setGlobalAiLoading(false);
  }
};

// Toolbar Modules
export const modules = {
  toolbar: {
    container: "#toolbar",
    handlers: {
      undo: undoChange,
      redo: redoChange,
      divide: addDivider,
      border: addBorder,
      star: insertStar,
      aiExpand: function (this: { quill: Quill }) {
        handleAiTransformation(this.quill, "expand", expandTextAiResponse);
      },
      aiShorten: function (this: { quill: Quill }) {
        handleAiTransformation(this.quill, "shorten", shortenTextAiResponse);
      },
      aiRephrase: function (this: { quill: Quill }) {
        handleAiTransformation(this.quill, "rephrase", rephraseTextAiResponse);
      },
      aiSimplify: function (this: { quill: Quill }) {
        handleAiTransformation(this.quill, "simplify", simplifyTextAiResponse);
      },
      aiBulletize: function (this: { quill: Quill }) {
        handleAiTransformation(
          this.quill,
          "bulletize",
          bulletizeTextAiResponse
        );
      },
    },
  },
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

export const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "align",
  "strike",
  "script",
  "blockquote",
  "background",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "color",
  "code-block",
  "em",
  "divider",
];

interface QuillToolbarProps {
  setShowError?: Dispatch<SetStateAction<boolean>>;
}

export const QuillToolbar: React.FC<QuillToolbarProps> = ({ setShowError }) => {
  const [isAiLoading, setIsAiLoading] = React.useState(false);

  React.useEffect(() => {
    setGlobalAiLoading = setIsAiLoading;

    return () => {
      setGlobalAiLoading = null;
    };
  }, []);

  return (
    <div
      id="toolbar"
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: "5px",
        padding: "5px",
        backgroundColor: "#fff",
      }}
    >
      <span className="ql-formats">
        <select className="ql-header" defaultValue="" title="Header">
          <option value="1">Heading 1</option>
          <option value="2">Heading 2</option>
          <option value="3">Heading 3</option>
          <option value="4">Heading 4</option>
          <option value="5">Heading 5</option>
          <option value="6">Heading 6</option>
          <option value="">Normal</option>
        </select>
      </span>

      <span className="ql-formats">
        <button className="ql-bold" title="Bold" />
        <button className="ql-italic" title="Italic" />
        <button className="ql-underline" title="Underline" />
        <button className="ql-strike" title="Strikethrough" />
      </span>

      <span className="ql-formats">
        <select className="ql-align" title="Align" />
        <select className="ql-color" title="Text Color">
          {colorsList.map((color) => (
            <option key={color} value={color} />
          ))}
        </select>
        <select className="ql-background" title="Background Color" />
      </span>

      <span className="ql-formats">
        <button className="ql-list" value="ordered" title="Ordered List" />
        <button className="ql-list" value="bullet" title="Bullet List" />
        <button className="ql-indent" value="-1" title="Decrease Indent" />
        <button className="ql-indent" value="+1" title="Increase Indent" />
      </span>

      <span
        style={{
          fontWeight: 500,
          opacity: isAiLoading ? 0.7 : 1,
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        {isAiLoading ? "Generating..." : "AI Tools -"}
        {isAiLoading && (
          <span
            className="spinner-border spinner-border-sm text-primary"
            role="status"
            aria-hidden="true"
            style={{ width: "14px", height: "14px" }}
          />
        )}
      </span>
      <span
        className="ql-formats"
        style={{
          opacity: isAiLoading ? 0.6 : 1,
          pointerEvents: isAiLoading ? "none" : "auto",
        }}
      >
        <button className="ql-aiExpand" title="Expand" type="button" disabled={isAiLoading}>
          <FaExpand />
        </button>
        <button className="ql-aiShorten" title="Shorten" type="button" disabled={isAiLoading}>
          <FaCompress />
        </button>
        <button className="ql-aiRephrase" title="Rephrase" type="button" disabled={isAiLoading}>
          <FaSyncAlt />
        </button>
        <button className="ql-aiSimplify" title="Simplify" type="button" disabled={isAiLoading}>
          <FaStream />
        </button>
        <button className="ql-aiBulletize" title="Bulletize" type="button" disabled={isAiLoading}>
          <FaListUl />
        </button>
      </span>
    </div>
  );
};

export default QuillToolbar;
